import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { CONFIG } from "@/config";
import { getUtm, newLeadId, postToSheet } from "@/lib/sheets";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(values) {
  const errors = {};
  if (values.name.trim().length < 2) errors.name = "Please enter your full name.";
  const digits = values.phone.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 13) errors.phone = "Enter a phone number with 10 to 13 digits.";
  if (!EMAIL_RE.test(values.email.trim())) errors.email = "Enter a valid email address.";
  return errors;
}

const Field = ({ id, label, error, ...props }) => (
  <div className="field">
    <label htmlFor={id} className="field-label">{label}</label>
    <input id={id} className={`field-input ${error ? "field-input-error" : ""}`} data-testid={`input-${id}`} {...props} />
    {error && <p className="field-error" data-testid={`error-${id}`}>{error}</p>}
  </div>
);

export const RegistrationForm = ({ onRegistered, embedded = false }) => {
  const g = CONFIG.gate;
  const [values, setValues] = useState({ name: "", phone: "", email: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | saving | failed
  const [attempts, setAttempts] = useState(0);

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const viewer = {
      name: values.name.trim(),
      phone: values.phone.trim(),
      email: values.email.trim(),
      leadId: newLeadId(),
    };
    setStatus("saving");
    try {
      await postToSheet({ type: "lead", ...viewer, ...getUtm(), page: window.location.href });
      onRegistered(viewer);
    } catch (err) {
      const n = attempts + 1;
      setAttempts(n);
      // One retry is allowed. After the second failure the viewer is let in anyway.
      if (n >= 2) onRegistered(viewer);
      else setStatus("failed");
    }
  };

  const saving = status === "saving";

  return (
    <form onSubmit={submit} noValidate className={embedded ? "relative" : "glass rounded-3xl p-6 sm:p-8 relative overflow-hidden"} data-testid="registration-form">
      {!embedded && <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-[#B6F23C]/10 blur-3xl" />}
      <div className="relative space-y-5">
        <Field id="name" label={g.nameLabel} value={values.name} onChange={set("name")} error={errors.name} autoComplete="name" placeholder="Priya Sharma" />
        <Field id="phone" label={g.phoneLabel} value={values.phone} onChange={set("phone")} error={errors.phone} autoComplete="tel" inputMode="tel" placeholder="+91 98765 43210" />
        <Field id="email" label={g.emailLabel} value={values.email} onChange={set("email")} error={errors.email} autoComplete="email" inputMode="email" placeholder="priya@example.com" />

        {status === "failed" && (
          <p className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/10 px-4 py-3 text-sm text-[#F4F5F2]" data-testid="register-error">
            {g.errorText}
          </p>
        )}

        <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="btn-register">
          {saving ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> {g.buttonLoadingText}</>
          ) : (
            <>{status === "failed" ? g.retryButtonText : g.buttonText} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>
          )}
        </button>
      </div>
    </form>
  );
};
